import { Icon, Input, Switch } from '@aon/aon-tws-common-ui';
import globalVars from '@aon/aon-tws-common-ui/build/lib/globalVars.module.scss';
import React, { useState } from 'react';
import _ from 'lodash';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { useAppDispatch } from '../../../appStore';
import customFlag from '../../../assets/images/icon-customFlag.png';
import ContentConstants from '../../../contentConstants';
import {
	EditState,
	ISurveyElement,
	ISurveyQuestionEditModel,
	IValidationCollection,
	SeverityType,
	SourceType,
	SurveyPropertyUnderValidation,
} from '../../../models';
import { IApplicationState } from '../../../store';
import { ActionTypes } from '../../../store/ActionTypes';
import { ValidationCollectionHelper } from '../../../utilities';
import { useBranchingAndLayout } from '../../../utilities/hooks';
import { FormattedTd, NumericToggleIcon } from '../../common';
import { CustomBanner } from '../../common/CustomBanner';
import { WarningBox, WarningIcon } from '../../common/warnings';
import { QuestionDisplayLogicDrawer } from './QuestionDisplayLogicDrawer';
import FollowUpQuestionRow from './table/FollowUpQuestionRow';

const lastChildWidth = (pageFlowElementsExist: boolean): string => {
	return pageFlowElementsExist
		? `
			width: 77px;
			min-width: 72px;
		`
		: `
			width: 25px;
			min-width: 25px;
		`;
};

const ContainerDiv = styled.div.attrs<
	{ isLastInRandomizedGroup: boolean; isFirstInRandomizedGroup: boolean; pageFlowElementsExist: boolean },
	any
>(() => ({
	className: 'tws-formatted-draggable-table-row',
}))`
	padding-bottom: ${(props) => (props.isLastInRandomizedGroup ? '30' : '10')}px;
	margin-top: ${(props) => (props.isFirstInRandomizedGroup ? '20' : '0')}px;
	margin-right: 0px;
	min-width: 500px;
	position: relative;

	display: flex;

	> :first-child {
		background-color: ${globalVars.tertiaryBlue2};
		border-bottom-left-radius: 4px;
		border-top-left-radius: 4px;
		padding: 0px;
		color: ${globalVars.white};
		display: flex;
		display: -ms-flexbox;
		display: -webkit-flex;
		align-items: center;
		justify-content: center;
		flex: 0.5 0.5 0%;
		-webkit-flex: 0.5 0.5 0%;
		-ms-flex: 0.5 0.5 0%;
		word-break: unset;
		min-width: 39px;
		width: 39px;
	}
	> :nth-child(2) {
		padding-left: 30px !important;
	}
	> :last-child {
		padding-left: 4px;
		${(props) => lastChildWidth(props)}
	}
`;

const RandomizedBorder = styled.div<{ expanded: boolean; randomizeGroupSize: number }>`
	border: 1px dashed #bbbbbb;
	border-radius: 2px;
	position: absolute;
	top: 0px;
	width: 100%;
	height: ${(props) => (props.expanded ? 90 * props.randomizeGroupSize + 20 + '' : 60 * props.randomizeGroupSize + 20 + '')}px;
	pointer-events: none;
`;
RandomizedBorder.displayName = 'RandomizedBorder';

const IconContainer = styled.div`
	cursor: pointer;
	font-size: 14px;
`;
IconContainer.displayName = 'IconContainer';

const DimensionTextStyling = styled.div.attrs<{}>({
	className: 'tws-design-survey-dimension-text',
})`
	font-size: 12px;
	font-weight: 500;
	margin-bottom: 12px;
`;
const QuestionTextStyling = styled.div.attrs<{}>({
	className: 'tws-design-survey-question-text',
})`
	font-weight: 600;
	line-height: 1.2em;
	color: ${globalVars.mainGray};
`;

const BranchError = styled.span`
	color: ${globalVars.tertiaryRed};
	position: absolute;
	left: 100%;
	bottom: 0;
	transform: translate(-50%, 50%);
`;
BranchError.displayName = 'BranchError';

const ErrorIcon = styled(Icon)`
	font-size: 18px;
	border-radius: 16px;
	border: 3px solid ${globalVars.secondaryBlue};
	border-top-color: ${globalVars.secondaryBlue};
	border-top-style: solid;
	border-top-width: 2px;
	border-right-color: ${globalVars.secondaryBlue};
	border-right-style: solid;
	border-right-width: 2px;
	border-bottom-color: ${globalVars.secondaryBlue};
	border-bottom-style: solid;
	border-bottom-width: 2px;
	border-left-color: ${globalVars.secondaryBlue};
	border-left-style: solid;
	border-left-width: 2px;
	border-image-source: initial;
	border-image-slice: initial;
	border-image-width: initial;
	border-image-outset: initial;
	border-image-repeat: initial;
`;

const Column = styled(FormattedTd)<{ expanded: boolean }>`
	${(props) => (props.expanded ? '' : 'padding: 10px 30px 10px 0;')}
	min-height: 40px;
`;
Column.displayName = 'Column';

const FirstColumn = styled(Column)<{ isInRandomizedGroup: boolean }>`
	${(props) => (props.isInRandomizedGroup ? 'margin-left: 14px;' : '')}
	.tws-input-text-wrapper {
		z-index: 999;
	}
`;
FirstColumn.displayName = 'FirstColumn';

const LastColumn = styled(Column)<{ isInRandomizedGroup: boolean }>`
	align-items: end;
	${(props) => (props.isInRandomizedGroup ? 'margin-right: 14px;' : '')}
`;
LastColumn.displayName = 'LastColumn';

const DisplayOrderInput = styled(Input).attrs<{}>({})<{}>`
	&.tws-input {
		background-color: transparent;
		border: 0;
		padding: 0;
		text-align: center;
		color: ${globalVars.white};
	}
`;
DisplayOrderInput.displayName = 'DisplayOrderInput';

const FollowUpRow = styled(FormattedTd)<{ expanded: boolean }>`
	${(props) => (props.expanded ? '' : 'padding: 10px 30px 10px 0;')}
	min-height: 40px;
`;
FollowUpRow.displayName = 'FollowUpRow';

const FollowupQuestionExpandExpandIcon = styled.div`
	background-color: ${globalVars.mainBlue};
	padding-top: 6px;
	padding-left: 12px;
	border-radius: 3px;
	padding-bottom: 6px;
	max-width: 35px;
`;
FollowupQuestionExpandExpandIcon.displayName = 'FollowupQuestionExpandExpandIcon';

export interface IQuestionDesignRowProps {
	ToggleDisplayLogicModal: (isOpen: boolean, surveyQuestionId: number) => void;
	question: ISurveyQuestionEditModel;
	questions?: ISurveyQuestionEditModel[];
	disableBranching: boolean;
	index: number;
	validationResults?: IValidationCollection;
	pageFlowWarnings?: string[];
	expanded: boolean;
	searchText: string;
	pageFlowElementsExist?: boolean;
	isInRandomizedGroup: boolean;
	isFirstInRandomizedGroup: boolean;
	isLastInRandomizedGroup: boolean;
	randomizeGroupSize: number;
	isRandomizeOn: boolean;
	handleVisibilityChange?: (question: ISurveyQuestionEditModel, isHidden: boolean) => void;
}

// This component is used on Logic and Layout page. QuestionRow component is used on Confirm and Schedule page.
export const QuestionDesignRow = (props: IQuestionDesignRowProps) => {
	const dispatch = useAppDispatch();
	const { handleSortingChange } = useBranchingAndLayout();
	const surveyEditModel = useSelector((state: IApplicationState) => state.survey.surveyEditModel);
	const isPageOneRandomized = surveyEditModel.questions.length > 0 && surveyEditModel.questions[0].isRandomized;

	const {
		ToggleDisplayLogicModal,
		disableBranching,
		expanded,
		isFirstInRandomizedGroup,
		isInRandomizedGroup,
		isLastInRandomizedGroup,
		isRandomizeOn,
		pageFlowElementsExist,
		pageFlowWarnings,
		question,
		randomizeGroupSize,
		validationResults,
	} = props;

	const [isDrawerOpen, setIsDrawerOpen] = useState(false);
	const [displayOrder, setDisplayOrder] = useState(question.displayOrder ?? 0);
	const [showFollowUpQuestion, setShowFollowUpQuestion] = useState(true);
	const followUpQuestion =
		surveyEditModel.questions && surveyEditModel.questions.find((x) => x.parentQuestionId && x.parentQuestionId === question.id);

	return (
		<>
			<ContainerDiv
				isFirstInRandomizedGroup={isFirstInRandomizedGroup}
				isLastInRandomizedGroup={isLastInRandomizedGroup}
				pageFlowElementsExist={pageFlowElementsExist}
				key={question.displayOrder ?? 0}
				className={'tws-formatted-draggable-table-row'}
			>
				{renderDisplayOrderColumn()}
				{renderQuestionTextColumn()}
				{(expanded || isRandomizeOn) && renderResponseOptionGroupColumn()}
				{expanded && renderHideQuestionColumn()}
				{renderFollowUpQuestionExpandColumn()}
				{renderBranchingContainer()}
			</ContainerDiv>
			{showFollowUpQuestion && followUpQuestion && followUpQuestion.editState !== EditState.Remove && (
				<FollowUpQuestionRow question={followUpQuestion}></FollowUpQuestionRow>
			)}
			{isFirstInRandomizedGroup && <RandomizedBorder randomizeGroupSize={randomizeGroupSize} expanded={expanded} />}
			{question.displayRulesCount > 0 && isDrawerOpen && (
				<QuestionDisplayLogicDrawer surveyQuestionId={question.surveyQuestionId ?? 0} ToggleDisplayLogicModal={toggleDisplayLogicModal} />
			)}
		</>
	);

	function handleDimensionName() {
		if (!question.dimensionNames) {
			return '';
		}

		const orderedDimensionNames = [...question.dimensionNames].sort((a, b) => a.localeCompare(b));
		return <DimensionTextStyling>{orderedDimensionNames.join(', ')}</DimensionTextStyling>;
	}

	function renderDisplayOrderColumn() {
		return (
			<FirstColumn flexBasis={0} expanded={expanded} className='tws-design-survey-sequence' isInRandomizedGroup={isInRandomizedGroup}>
				<DisplayOrderInput
					onChange={handleDisplayOrderChange}
					maxLength={3}
					size={3}
					onBlur={handleReorder}
					onKeyUp={handleReorder}
					value={displayOrder}
				/>
				{question.sourceType === SourceType.Custom && <CustomBanner src={customFlag} />}
			</FirstColumn>
		);
	}

	function renderQuestionTextColumn() {
		return (
			<Column flexBasis={expanded ? 50 : 80} expanded={expanded} className='tws-design-survey-question'>
				{expanded && handleDimensionName()}
				<QuestionTextStyling>{question.text}</QuestionTextStyling>
				{pageFlowWarnings && pageFlowWarnings.length > 0 && <WarningBox severity={SeverityType.Warning} messages={pageFlowWarnings} />}
			</Column>
		);
	}

	function renderHideQuestionColumn() {
		return (
			<Column flexBasis={13} expanded={expanded}>
				<Switch
					checked={question.isHidden}
					disabled={question.isRequired}
					getValue={(isHidden: boolean) => dispatch({ type: ActionTypes.HIDE_QUESTION_TOGGLE, payload: { isHidden, question } })}
				/>
			</Column>
		);
	}

	function renderResponseOptionGroupColumn() {
		return (
			<Column flexBasis={25} expanded={expanded} className='tws-design-survey-response-option'>
				{question.responseOptionGroup}
			</Column>
		);
	}
	function renderBranchingContainer() {
		return (
			<LastColumn flexBasis={10} expanded={expanded} className='tws-design-survey-branching' isInRandomizedGroup={isInRandomizedGroup}>
				{!disableBranching ? getBranchingIconContainer() : null}
			</LastColumn>
		);
	}

	function getBranchingIconContainer() {
		return (
			<div className='tws-branching-icon-container position-relative'>
				{question.displayRulesCount > 0 ? getToggleBranchingIcon() : getAddBranchingIcon()}
				{hasError() && (
					<BranchError>
						<ErrorIcon name='error' />
					</BranchError>
				)}
			</div>
		);
	}

	function getAddBranchingIcon() {
		return (
			<NumericToggleIcon
				id={question.id.toString()}
				open={false}
				onClick={() => props.ToggleDisplayLogicModal(true, question.surveyQuestionId!)}
				iconOpen='add'
				iconClosed='add'
			/>
		);
	}

	function getToggleBranchingIcon() {
		return <NumericToggleIcon id={question.id.toString()} count={question.displayRulesCount} open={isDrawerOpen} onClick={toggleDrawer} />;
	}

	function renderFollowUpQuestionExpandColumn() {
		const showWarning =
			!showFollowUpQuestion && followUpQuestion && (!followUpQuestion.responseOptions || followUpQuestion.responseOptions.length === 0);
		const style = {
			backgroundColor: showFollowUpQuestion ? globalVars.mainBlue : '#edf4f7',
			color: showFollowUpQuestion ? 'white' : 'black',
			minWidth: '36px',
			marginLeft: '10px',
			border: showFollowUpQuestion || !showWarning ? 'none' : 'solid 1px ' + globalVars.mainBlue,
		};
		return (
			<FollowUpRow flexBasis={10} expanded={expanded}>
				{followUpQuestion && followUpQuestion.editState !== EditState.Remove && (
					<>
						<FollowupQuestionExpandExpandIcon onClick={() => setShowFollowUpQuestion(!showFollowUpQuestion)} style={style}>
							<Icon name={showFollowUpQuestion ? 'angle-up' : 'angle-down'} style={{ fontSize: '12px' }} />
						</FollowupQuestionExpandExpandIcon>
						{!showFollowUpQuestion && showWarning && <WarningIcon name={'error'} />}
					</>
				)}
			</FollowUpRow>
		);
	}

	function handleDisplayOrderChange(e: any) {
		if (!isNaN(e.target.value) && isWithinRange(e.target.value as number)) {
			setDisplayOrder(e.target.value as number);
		}
	}

	function isWithinRange(value: number) {
		return !value || (value > 0 && value <= surveyEditModel.questions.length);
	}

	function moveQuestion(questions: ISurveyElement[], old_index: number, new_index: number) {
		questions.splice(new_index, 0, questions.splice(old_index, 1)[0]);
		return questions;
	}

	function handleReorder(e: any) {
		if (e.key === 'Enter' || e.type === 'blur') {
			if (isNaN(e.target.value) ?? !e.target.value) {
				dispatch({ type: ActionTypes.TOAST_INFO_NOTIFICATION, payload: ContentConstants.TOAST_INVALID_NUMBER });
				setDisplayOrder(question.displayOrder ?? 0);
				return;
			}

			const questions = _.cloneDeep(surveyEditModel.questions);
			const targetQuestion = questions.find((q) => q.displayOrder?.toString() === e.target.value) ?? questions.slice(-1)[0];
			const targetQuestionId = targetQuestion.id;

			const elements = _.cloneDeep(surveyEditModel.pageFlowElements as ISurveyElement[])
				.concat(questions)
				.sort((e1, e2) => (e1.sequence ?? 0) - (e2.sequence ?? 0));

			const origIndex = elements.findIndex((ele) => ele.id === question.id);
			const targetIndex = elements.findIndex((ele) => ele.id === targetQuestionId);
			const newElements = moveQuestion(elements, origIndex, targetIndex);

			const itemIds = newElements.map((ele) => ele.id.toString());
			handleSortingChange(itemIds, isPageOneRandomized ?? false);
		}
	}

	function toggleDrawer() {
		setIsDrawerOpen(!isDrawerOpen);
	}

	function toggleDisplayLogicModal(isOpen: boolean, surveyQuestionId: number) {
		ToggleDisplayLogicModal(isOpen, surveyQuestionId);
		toggleDrawer();
	}

	function hasError() {
		const validationResult = new ValidationCollectionHelper(validationResults).GetValidationResultForProperty(
			SurveyPropertyUnderValidation.displayLogic,
		);

		if (!validationResult || !question.surveyQuestionId) {
			return false;
		}

		if (
			validationResult.propertyErrors &&
			Object.prototype.hasOwnProperty.call(validationResult.propertyErrors, question.surveyQuestionId)
		) {
			return validationResult.propertyErrors[question.surveyQuestionId].length > 0;
		}

		return false;
	}
};
